import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthState } from '@/hooks/useAuth';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { PageviewTracker } from '@/components/PageviewTracker';
import { CookieConsent } from '@/components/CookieConsent';
import { lazyWithRetry } from '@/lib/lazyWithRetry';

// Auth pages (public)
const SignUp = lazyWithRetry(() => import('./pages/SignUp'));
const OperatorConsole = lazyWithRetry(() => import('./pages/OperatorConsole'));
const TwoFA = lazyWithRetry(() => import('./pages/TwoFA'));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'));

// Error pages (public)
const NotFoundPage = lazyWithRetry(() => import('./pages/NotFoundPage'));

// Workspace (the product)
const Workspace = lazyWithRetry(() => import('./pages/Workspace'));

// AppShell pages (legacy — these will be ported into the Workspace tab system)
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const StockEarnings = lazyWithRetry(() => import('./pages/StockEarnings'));
const ActivityFeed = lazyWithRetry(() => import('./pages/ActivityFeed'));
const OnecActionLog = lazyWithRetry(() => import('./pages/OnecActionLog'));
const Log = lazyWithRetry(() => import('./pages/Log'));
const OnecEntityTrace = lazyWithRetry(() => import('./pages/OnecEntityTrace'));
const DocumentViewer = lazyWithRetry(() => import('./pages/DocumentViewer'));
const CompanyProfile = lazyWithRetry(() => import('./pages/CompanyProfile'));

// Audit pages
const AuditEngagements = lazyWithRetry(() => import('./pages/AuditEngagements'));
const AuditProject = lazyWithRetry(() => import('./pages/AuditProject'));
const AuditChat = lazyWithRetry(() => import('./pages/AuditChat'));
const AuditAutoAnalysis = lazyWithRetry(() => import('./pages/AuditAutoAnalysis'));
const AuditSampling = lazyWithRetry(() => import('./pages/AuditSampling'));

// Settings live in the WorkspaceCore modal (see SETTINGS_SECTIONS). The old
// standalone /settings/* pages were removed — the modal is the single source.
const AppShell = lazyWithRetry(() => import('@/layouts/AppShell'));

// Sentry debug — not linked anywhere, reachable by direct URL for
// ops verification (/debug-sentry). Kept out of nav + sitemap.
const SentryDebug = lazyWithRetry(() => import('./pages/SentryDebug'));

// Galter Beta — minimal chat surface that talks to Anthropic's Managed
// Agents runtime. Reachable at /galter-beta. Behind the backend's
// MANAGED_AGENTS_ENABLED flag.
const GalterBeta = lazyWithRetry(() => import('./pages/GalterBeta'));

// Welcome — post-invite / password-recovery set-password page.
// Intentionally routed OUTSIDE <PublicRoute> so authenticated
// users (which invite-clickers are, per Supabase's hash flow) can
// still reach the set-password form instead of being redirected
// to the workspace before their password exists.
const Welcome = lazyWithRetry(() => import('./pages/Welcome'));
const ConnectClaude = lazyWithRetry(() => import('./pages/ConnectClaude'));

function ProtectedRoute() {
  const auth = useAuthState();
  if (auth.status === 'pending') return <Loading />;
  if (auth.status === 'unauthenticated') return <Navigate to="/login" replace />;
  if (auth.status === 'needs-mfa') return <Navigate to="/2fa" replace />;
  return <Outlet />;
}

function PublicRoute() {
  const auth = useAuthState();
  if (auth.status === 'pending') return <Loading />;
  if (auth.status === 'needs-mfa') return <Navigate to="/2fa" replace />;
  // Authenticated users land on the workspace root
  if (auth.status === 'authenticated') return <Navigate to="/" replace />;
  return <Outlet />;
}

function MfaRoute() {
  const auth = useAuthState();
  if (auth.status === 'pending') return <Loading />;
  if (auth.status === 'unauthenticated') return <Navigate to="/login" replace />;
  if (auth.status === 'authenticated') return <Navigate to="/" replace />;
  return <Outlet />;
}

function Loading() {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <span className="text-body font-body text-subtext-color">Loading...</span>
    </div>
  );
}

/** Old /signup links (emails, bookmarks, landing) land on the canonical /login, query and hash intact. */
function LegacySignupRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: '/login', search, hash }} replace />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <PageviewTracker />
        <CookieConsent />
        <Suspense fallback={<Loading />}>
          <Routes>
            {/* ── Auth routes (public) ────────────────────────────── */}
            <Route element={<PublicRoute />}>
              {/* /login is canonical: Safari/iCloud Keychain classify a page served at
                  "/signup" as registration and offer a new password instead of the saved one. */}
              <Route path="/login" element={<SignUp />} />
              <Route path="/signup" element={<LegacySignupRedirect />} />
              <Route path="/reset-password" element={<ResetPassword />} />
            </Route>

            <Route element={<MfaRoute />}>
              <Route path="/2fa" element={<TwoFA />} />
            </Route>

            {/* ── Workspace — the product ─────────────────────────
                Root "/" is the workspace. Auth gating happens inside
                Workspace.tsx. Conversation URLs use /c/:id for brevity. */}
            <Route path="/" element={<Workspace />} />
            <Route path="/c/:conversationId" element={<Workspace />} />

            {/* Sentry debug — direct URL only, not linked anywhere. */}
            <Route path="/debug-sentry" element={<SentryDebug />} />

            {/* Galter Beta — Anthropic Managed Agents runtime, behind
                MANAGED_AGENTS_ENABLED on the backend. Direct URL only. */}
            <Route path="/galter-beta" element={<GalterBeta />} />

            {/* Welcome — post-invite set-password page. Must be
                reachable when authenticated (Supabase hash flow
                signs the user in before they've chosen a password). */}
            <Route path="/welcome" element={<Welcome />} />

            {/* ── Legacy route redirects ──────────────────────────
                Old bookmarks and external links still work. */}
            <Route path="/home-chat" element={<Navigate to="/" replace />} />
            <Route path="/workspace" element={<Navigate to="/" replace />} />
            <Route
              path="/chat/:conversationId"
              element={<Navigate to="/c/:conversationId" replace />}
            />
            {/* Closing month is now a workspace function — old bookmarks
                land at workspace root which auto-opens the closing pane
                via the ?closing=1 query param honoured by Workspace.tsx. */}
            <Route path="/close-month" element={<Navigate to="/?closing=1" replace />} />
            <Route path="/close-month/:runId" element={<Navigate to="/?closing=1" replace />} />

            {/* ── AppShell pages (protected) ──────────────────────
                Dashboard, audit, data pages, and legacy settings. */}
            <Route element={<ProtectedRoute />}>
              {/* Claude connector OAuth consent — protected, full-screen (no AppShell chrome) */}
              <Route path="/connect-claude" element={<ConnectClaude />} />
              {/* Operator console — AltaiLabs staff (403 for everyone else) */}
              <Route path="/admin" element={<OperatorConsole />} />
              <Route element={<AppShell />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/tax-overview" element={<StockEarnings />} />
                <Route path="/activity/:id" element={<ActivityFeed />} />
                <Route path="/onec/action-log" element={<OnecActionLog />} />
                <Route path="/log" element={<Log />} />
                <Route path="/onec/entity/:entityType/:refKey" element={<OnecEntityTrace />} />
                <Route path="/documents/:id" element={<DocumentViewer />} />
                <Route path="/counterparties/:id" element={<CompanyProfile />} />

                <Route path="/audit" element={<AuditEngagements />} />
                <Route path="/audit/:id" element={<AuditProject />} />
                <Route path="/audit/:id/chat" element={<AuditChat />} />
                <Route path="/audit/:id/analysis" element={<AuditAutoAnalysis />} />
                <Route path="/audit/:id/sampling" element={<AuditSampling />} />
              </Route>
            </Route>

            {/* ── Catchall → 404 ──────────────────────────────────
                Any unmatched URL renders the NotFoundPage so stale
                bookmarks and typos surface as explicit "not here
                anymore" copy instead of silently redirecting to /
                (which used to mask auth-redirect loops and made
                debugging dead URLs harder than it needed to be). */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
